import React from "react";
import {DateTime} from "luxon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarPlus, faLocationDot, faStar} from "@fortawesome/free-solid-svg-icons";
import {getCalendarMonthGridDates} from "../../helpers/calendar/helpers";
import {pluralize} from "../../helpers/helpers";
import type {CalendarMonthGridProps} from "./types";
import "./calendar-month-grid.scss";

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CalendarMonthGrid({year, month, days, biggestDayDate, selectedDate, onSelectDate}: CalendarMonthGridProps) {
    const dates = getCalendarMonthGridDates(year, month);

    return (
        <section className="calendar-month-grid" aria-label={`${DateTime.local(year, month).toFormat('LLLL yyyy')} birding activity`}>
            <div className="calendar-month-grid__weekdays" aria-hidden="true">
                {WEEKDAY_LABELS.map(day => <span key={day}>{day}</span>)}
            </div>
            <div className="calendar-month-grid__days">
                {dates.map(date => {
                    const dateTime = DateTime.fromISO(date);
                    const summary = days[date];
                    const isCurrentMonth = dateTime.month === month;
                    const isSelected = date === selectedDate;
                    const isBiggestDay = date === biggestDayDate;
                    const label = summary
                        ? `${dateTime.toFormat('cccc, LLLL d')}: ${summary.speciesCount} ${pluralize(summary.speciesCount, "species")}, ${summary.checklistCount} ${pluralize(summary.checklistCount, "checklist")}${summary.liferCount ? `, ${summary.liferCount} ${pluralize(summary.liferCount, "lifer")}` : ''}${summary.firstOfYearCount ? `, ${summary.firstOfYearCount} ${pluralize(summary.firstOfYearCount, "year bird")}` : ''}${isBiggestDay ? ', biggest day of the month' : ''}`
                        : dateTime.toFormat('cccc, LLLL d');

                    return (
                        <button
                            type="button"
                            key={date}
                            className={`calendar-month-grid__day ${isCurrentMonth ? '' : 'is-outside-month'} ${summary ? 'has-activity' : ''} ${isBiggestDay ? 'is-biggest-day' : ''} ${isSelected ? 'is-selected' : ''}`.trim()}
                            onClick={() => onSelectDate(date)}
                            aria-label={label}
                            aria-pressed={isSelected}
                        >
                            <span className="calendar-month-grid__date">{dateTime.day}</span>
                            {isBiggestDay ? <span className="calendar-month-grid__big-day-label">Big day</span> : null}
                            {summary ? <span className="calendar-month-grid__metrics">
                                <span>{summary.speciesCount} <small>species</small></span>
                                <span><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /> {summary.locationCount}</span>
                                {summary.liferCount ? <span className="calendar-month-grid__lifer"><FontAwesomeIcon icon={faStar} aria-hidden="true" /> {summary.liferCount}</span> : null}
                                {summary.firstOfYearCount ? <span className="calendar-month-grid__year-bird"><FontAwesomeIcon icon={faCalendarPlus} aria-hidden="true" /> {summary.firstOfYearCount}</span> : null}
                            </span> : null}
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
